import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Copy, TrendingUp, WalletCards, Sparkles, Activity, Users, PiggyBank, Zap } from "lucide-react";
import { MemberLayout } from "../../components/Layouts";
import { MiningGame } from "../../components/MiningGame";
import { api, readUser, fmt } from "../../lib/api";

const STAT_META = [
  { key: "total_production", label: "Total production", note: "across daily credits", icon: Zap, accent: "aqua" },
  { key: "active_packages", label: "Active packages", note: "currently running", icon: Activity, accent: "mint" },
  { key: "total_referral_earnings", label: "Referral earnings", note: "across 4 levels", icon: Users, accent: "amber" },
  { key: "total_deposits", label: "Total deposits", note: "verified activity", icon: PiggyBank, accent: "violet" },
];

function AnimatedNumber({ value, prefix = "", decimals = 2 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = Number(value) || 0;
    const start = display;
    const diff = target - start;
    if (Math.abs(diff) < 0.001) { setDisplay(target); return; }
    const steps = 24;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      const t = i / steps;
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(start + diff * eased);
      if (i >= steps) { clearInterval(id); setDisplay(target); }
    }, 24);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <>{prefix}{Number(display).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</>;
}

export default function Dashboard() {
  const [user, setUser] = useState(readUser());
  const [data, setData] = useState({});
  const [ref, setRef] = useState({});
  const [recent, setRecent] = useState([]);
  const [copied, setCopied] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => { setUser(r.data); localStorage.setItem("ha_user", JSON.stringify(r.data)); }).catch(() => nav("/login"));
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/referrals").then((r) => setRef(r.data)).catch(() => {});
    api.get("/transactions").then((r) => setRecent((r.data || []).slice(0, 6))).catch(() => {});
  }, [nav]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5) return "You're up early";
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  const spark = useMemo(() => {
    // Build a 12-point sparkline from recent transactions (reverse chronological).
    if (!recent.length) return [8, 14, 10, 18, 16, 22, 20, 28, 26, 32, 30, 38];
    const pts = recent.slice().reverse().map((t) => Math.abs(Number(t.amount) || 0));
    while (pts.length < 12) pts.unshift(0);
    const max = Math.max(1, ...pts);
    return pts.slice(-12).map((v) => Math.round((v / max) * 36) + 4);
  }, [recent]);

  function copyRef() {
    const code = ref.referral_code || user?.referral_code || "";
    if (!code) return;
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function updateBalance(balance) {
    setData((current) => ({ ...current, available_balance: balance, total_balance: balance }));
    setUser((current) => current ? { ...current, balance } : current);
  }

  return (
    <MemberLayout user={user} active="Dashboard">
      <div className="dash-greeting dash-greeting-live">
        <div>
          <span className="eyebrow"><span className="pulse" /> LIVE · YOUR HELPING SPACE</span>
          <h2>{greeting}, <em>{user?.full_name?.split(" ")[0] || "friend"}</em>.</h2>
          <p>Keep an eye on your packages, your network, and the help you are contributing — all in one calm place.</p>
        </div>
        <div className="greeting-chip" data-testid="greeting-chip">
          <Sparkles size={14} /> {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
        </div>
      </div>

      <div className="balance-banner balance-banner-pro">
        <div className="balance-main">
          <span><span className="pulse" /> AVAILABLE BALANCE · USDT</span>
          <strong data-testid="dashboard-balance">$<AnimatedNumber value={data.available_balance} decimals={3} /></strong>
          <small><TrendingUp size={14} /> Ready to allocate across packages</small>
        </div>
        <div className="balance-spark" aria-hidden="true">
          <svg viewBox="0 0 240 60" preserveAspectRatio="none" className="spark-svg">
            <defs>
              <linearGradient id="sparkG" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#5dd5f3" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#5dd5f3" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={`M0,${60 - spark[0]} ${spark.map((y, i) => `L${(i * 240) / (spark.length - 1)},${60 - y}`).join(" ")} L240,60 L0,60 Z`} fill="url(#sparkG)" />
            <path d={`M0,${60 - spark[0]} ${spark.map((y, i) => `L${(i * 240) / (spark.length - 1)},${60 - y}`).join(" ")}`} fill="none" stroke="#5dd5f3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <Link data-testid="dashboard-deposit-button" to="/dashboard/deposits" className="btn btn-accent">Packages <ArrowRight size={16} /></Link>
      </div>

      <div className="dash-grid">
        {STAT_META.map((m, i) => {
          const Icon = m.icon;
          const val = data[m.key];
          return (
            <div className={`stat-card stat-card-pro accent-${m.accent}`} data-testid={`dashboard-stat-${i}`} key={m.key} style={{ animationDelay: `${i * 60}ms` }}>
              <div className="stat-head">
                <span>{m.label}</span>
                <div className="stat-icon"><Icon size={16} /></div>
              </div>
              <strong>
                {i === 1 ? (val || 0) : <>$<AnimatedNumber value={val} decimals={2} /></>}
              </strong>
              <small>{m.note}</small>
              <div className="stat-glow" />
            </div>
          );
        })}
      </div>

      <div className="member-columns">
        <section className="panel panel-pro">
          <div className="panel-heading">
            <div><span className="eyebrow">YOUR NETWORK</span><h3>Referral momentum</h3></div>
            <Link to="/dashboard/referrals" data-testid="referral-view-link">View network <ArrowRight size={14} /></Link>
          </div>
          <div className="referral-code">
            <div>
              <span>MY REFERRAL CODE</span>
              <strong data-testid="my-referral-code">{ref.referral_code || user?.referral_code || "—"}</strong>
            </div>
            <button data-testid="copy-referral-code-button" className={`icon-btn ${copied ? "copied" : ""}`} onClick={copyRef} title="Copy referral code">
              <Copy size={16} />
            </button>
          </div>
          <div className="level-bars">
            {[1, 2, 3, 4].map((l) => (
              <div key={l}>
                <span>Level {l}</span>
                <div><i style={{ width: `${Math.min(100, (ref.counts?.[l] || 0) * 24 + 8)}%` }} /></div>
                <b>{ref.counts?.[l] || 0}</b>
              </div>
            ))}
          </div>
        </section>
        <section className="panel panel-pro activity">
          <div className="panel-heading">
            <div><span className="eyebrow">LATEST</span><h3>Transaction activity</h3></div>
            <span className="green-dot"><span className="pulse" /> Live</span>
          </div>
          {recent.length ? (
            <ul className="activity-list activity-list-pro">
              {recent.map((t, i) => (
                <li key={i} data-testid={`activity-${i}`} style={{ animationDelay: `${i * 50}ms` }}>
                  <span className="activity-dot" />
                  <span className="activity-type">{t.type}</span>
                  <span className={`activity-amount ${t.amount < 0 ? "neg" : "pos"}`}>${fmt(t.amount, t.type === "Mining Reward" ? 3 : 2)}</span>
                  <small>{new Date(t.date).toLocaleString()}</small>
                </li>
              ))}
            </ul>
          ) : (
            <div className="empty-activity">
              <WalletCards size={28} />
              <p>Your verified activity will appear here.</p>
              <span>Activate a package to begin.</span>
            </div>
          )}
        </section>
      </div>
      <MiningGame onBalanceChange={updateBalance} />
    </MemberLayout>
  );
}
